import { bindTextures, createProgram, emptyVAO, uniforms, type GLContext } from '../gl/context';
import type { FlockSim } from '../sim/flock';
import type { PredatorState } from '../sim/predator';
import { BIRDS_FS, BIRDS_VS, PHOTO_FS, PHOTO_VS } from '../shaders/render';
import type { Camera } from './camera';
import type { Photo } from './photo';

export interface CoverFit {
  /** uv = screenUv * scale + offset */
  scale: [number, number];
  offset: [number, number];
}

/** Object-fit: cover, for a photo of pw×ph shown in a viewport of vw×vh. */
export function coverFit(pw: number, ph: number, vw: number, vh: number): CoverFit {
  const pa = pw / ph;
  const va = vw / Math.max(1, vh);
  if (va > pa) {
    const f = pa / va;
    return { scale: [1, f], offset: [0, (1 - f) / 2] };
  }
  const f = va / pa;
  return { scale: [f, 1], offset: [(1 - f) / 2, 0] };
}

export function photoToScreenY(fit: CoverFit, photoY: number): number {
  return (photoY - fit.offset[1]) / fit.scale[1];
}

export function screenToPhotoY(fit: CoverFit, screenY: number): number {
  return screenY * fit.scale[1] + fit.offset[1];
}

export interface BirdDrawOptions {
  birdSize: number;
  hazeDensity: number;
  birdColor: [number, number, number];
  /** Tone of a fully lit wing; kept dim, starlings in low sun are warm grey-brown. */
  litColor: [number, number, number];
  lightDir: [number, number, number];
  hazeColor: [number, number, number];
  /** Global fade, 0..1. */
  alpha: number;
  /** Colour birds by agitation instead of light. */
  debug: boolean;
}

/** Peregrine: 1.0 m span in multiples of a starling's 0.38 m; body 0.45 m rather than the proportional 0.58 m. */
const FALCON_SIZE = 1.0 / 0.38;
const FALCON_BODY = 0.45 / (0.22 * FALCON_SIZE);

export class SceneRenderer {
  private readonly gl: WebGL2RenderingContext;
  /** Flock sprites are capped for fill cost; the single falcon may use the hardware limit. */
  private readonly maxPoint: number;
  private readonly hwMaxPoint: number;
  private readonly photoProg: WebGLProgram;
  private readonly birdProg: WebGLProgram;
  private readonly uP;
  private readonly uB;
  private readonly vao: WebGLVertexArrayObject;

  constructor(ctx: GLContext) {
    const gl = ctx.gl;
    this.gl = gl;
    this.maxPoint = Math.min(96, ctx.maxPointSize);
    this.hwMaxPoint = ctx.maxPointSize;
    this.photoProg = createProgram(gl, PHOTO_VS, PHOTO_FS, 'photo');
    this.birdProg = createProgram(gl, BIRDS_VS, BIRDS_FS, 'birds');
    this.uP = uniforms(gl, this.photoProg, ['uUvScale', 'uUvOffset', 'uPhoto', 'uAlpha'] as const);
    this.uB = uniforms(gl, this.birdProg, [
      'uPos', 'uVel', 'uAux', 'uSimSize', 'uProj', 'uView', 'uPxPerMeterAt1', 'uBirdSize', 'uHazeDensity', 'uMaxPoint',
      'uCamPos', 'uLightDir', 'uSingle', 'uSinglePos', 'uSingleVel', 'uSingleAux',
      'uBirdColor', 'uLitColor', 'uHazeColor', 'uAlpha', 'uDebug',
    ] as const);
    this.vao = emptyVAO(gl);
  }

  begin(viewW: number, viewH: number): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, viewW, viewH);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.vao);
  }

  /** Draws a photo with cover fit. alpha < 1 blends over what is already there. */
  drawPhoto(photo: Photo | null, fit: CoverFit, alpha = 1): void {
    const gl = this.gl;
    if (!photo) {
      gl.clearColor(0.55, 0.6, 0.68, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      return;
    }
    if (alpha < 1) {
      gl.enable(gl.BLEND);
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }
    gl.useProgram(this.photoProg);
    bindTextures(gl, [photo.tex]);
    gl.uniform1i(this.uP.uPhoto, 0);
    gl.uniform2fv(this.uP.uUvScale, fit.scale);
    gl.uniform2fv(this.uP.uUvOffset, fit.offset);
    gl.uniform1f(this.uP.uAlpha, alpha);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disable(gl.BLEND);
  }

  drawBirds(sim: FlockSim, cam: Camera, o: BirdDrawOptions): void {
    const gl = this.gl;
    this.beginBirds(cam, o);
    bindTextures(gl, [sim.pos, sim.vel, sim.aux]);
    gl.uniform2i(this.uB.uSimSize, sim.texW, sim.texH);
    gl.uniform1i(this.uB.uSingle, 0);
    gl.drawArrays(gl.POINTS, 0, sim.active);
    gl.disable(gl.BLEND);
  }

  drawFalcon(cam: Camera, f: PredatorState, o: BirdDrawOptions): void {
    if (!f.visible) return;
    const gl = this.gl;
    this.beginBirds(cam, o);
    gl.uniform1i(this.uB.uSingle, 1);
    gl.uniform1f(this.uB.uMaxPoint, this.hwMaxPoint);
    gl.uniform4f(this.uB.uSinglePos, f.pos[0], f.pos[1], f.pos[2], f.phase);
    gl.uniform4f(this.uB.uSingleVel, f.vel[0], f.vel[1], f.vel[2], f.fold);
    gl.uniform4f(this.uB.uSingleAux, f.roll, FALCON_SIZE, f.glide, FALCON_BODY);
    gl.drawArrays(gl.POINTS, 0, 1);
    gl.disable(gl.BLEND);
  }

  private beginBirds(cam: Camera, o: BirdDrawOptions): void {
    const gl = this.gl;
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(this.birdProg);
    const U = this.uB;
    gl.uniform1i(U.uPos, 0);
    gl.uniform1i(U.uVel, 1);
    gl.uniform1i(U.uAux, 2);
    gl.uniformMatrix4fv(U.uProj, false, cam.proj);
    gl.uniformMatrix4fv(U.uView, false, cam.view);
    gl.uniform1f(U.uPxPerMeterAt1, cam.pxPerMeterAt1);
    gl.uniform1f(U.uBirdSize, o.birdSize);
    gl.uniform1f(U.uHazeDensity, o.hazeDensity);
    gl.uniform1f(U.uMaxPoint, this.maxPoint);
    gl.uniform3fv(U.uCamPos, cam.pos);
    gl.uniform3fv(U.uLightDir, o.lightDir);
    gl.uniform3fv(U.uBirdColor, o.birdColor);
    gl.uniform3fv(U.uLitColor, o.litColor);
    gl.uniform3fv(U.uHazeColor, o.hazeColor);
    gl.uniform1f(U.uAlpha, o.alpha);
    gl.uniform1i(U.uDebug, o.debug ? 1 : 0);
  }
}
